/**
 * Links for "Share from my device" (spec §2.2): the server renders the
 * message, the dashboard turns it into wa.me, sms: and mailto: links. Phone
 * normalisation and SMS segment counting mirror the server
 * (lambda/invoices/phone.mjs toE164, templates.mjs smsSegments) so the
 * numbers the shop sees here match what Netlise would send.
 */

/** E.164, Sri Lanka first: `077 312 4567` → `+94773124567`; invalid → null. */
export function toE164(raw?: string | null, countryCode = '+94'): string | null {
  if (!raw) return null;
  let digits = String(raw).replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return /^\+[1-9]\d{6,14}$/.test(digits) ? digits : null;
  if (digits.startsWith('00')) return toE164(`+${digits.slice(2)}`);
  const cc = String(countryCode || '+94').replace(/[^\d]/g, '');
  if (cc === '94') {
    if (digits.startsWith('94')) digits = digits.slice(2);
    if (digits.startsWith('0')) digits = digits.slice(1);
    return /^\d{9}$/.test(digits) ? `+94${digits}` : null;
  }
  digits = digits.replace(/^0+/, '');
  const e164 = `+${cc}${digits}`;
  return /^\+[1-9]\d{6,14}$/.test(e164) ? e164 : null;
}

/** Sri Lankan mobiles are +947…; 011… and other area codes are landlines. */
export const isSriLankaLandline = (e164?: string | null) => !!e164 && /^\+94\d{9}$/.test(e164) && !/^\+947\d{8}$/.test(e164);

export function waUrl(phoneE164: string | null | undefined, text: string): string {
  const digits = (phoneE164 || '').replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

/** `?&body=` is the form both iOS and Android Messages read. */
export function smsUrl(phoneE164: string | null | undefined, text: string): string {
  return `sms:${phoneE164 || ''}?&body=${encodeURIComponent(text)}`;
}

const EMAIL_RE = /^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/;
export const isEmail = (v?: string | null) => !!v && EMAIL_RE.test(v.trim());

export function mailtoUrl(email: string | null | undefined, subject: string, body: string): string {
  const to = isEmail(email) ? email!.trim() : '';
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// GSM 03.38: the basic set, and the extension set whose characters cost two.
const GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXT = '^{}\\[~]|€\f';

export interface SmsInfo { encoding: 'GSM7' | 'UCS2'; characters: number; segments: number }

/** Encoding, characters (septets for GSM-7, UTF-16 units for UCS-2) and segments. */
export function smsSegments(text?: string | null): SmsInfo {
  const s = String(text || '');
  let septets = 0;
  let gsm = true;
  for (const ch of s) {
    if (GSM_BASIC.includes(ch)) septets += 1;
    else if (GSM_EXT.includes(ch)) septets += 2;
    else { gsm = false; break; }
  }
  if (gsm) return { encoding: 'GSM7', characters: septets, segments: septets <= 160 ? 1 : Math.ceil(septets / 153) };
  const units = s.length;
  return { encoding: 'UCS2', characters: units, segments: units <= 70 ? 1 : Math.ceil(units / 67) };
}

/**
 * The message without its link, for previews that show the link as a
 * button: drops the URL and a short label right before it ("View and pay:",
 * "බලා ගෙවන්න:").
 */
export function withoutLink(text: string, link?: string | null): string {
  if (!link || !text.includes(link)) return text.trim();
  const at = text.lastIndexOf(link);
  let head = text.slice(0, at).replace(/\s+$/, '');
  const tail = text.slice(at + link.length).trim();
  const label = /(^|[.!?\n])([^.!?\n]{1,30}):$/.exec(head);
  if (label) head = head.slice(0, label.index + label[1].length).replace(/\s+$/, '');
  return [head, tail].filter(Boolean).join(head && tail ? ' ' : '').trim();
}
